import React from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import CreatableSelect from 'react-select/creatable';

import { messages } from '../../../_shared/i18n/messages';

import './TimePicker.scss';

const buildOption = (label) => ({ label, value: label });

// TODO: Internationalize this?
const TIME_OPTIONS = [
	buildOption('10:00am'),
	buildOption('10:30am'),
	buildOption('11:00am'),
	buildOption('11:30am'),
	buildOption('12:00pm'),
	buildOption('12:30pm'),
	buildOption('1:00pm'),
	buildOption('1:30pm'),
	buildOption('2:00pm'),
	buildOption('2:30pm'),
	buildOption('3:00pm'),
	buildOption('3:30pm'),
	buildOption('4:00pm'),
	buildOption('4:30pm'),
	buildOption('5:00pm'),
	buildOption('5:30pm'),
	buildOption('6:00pm'),
	buildOption('6:30pm'),
	buildOption('7:00pm'),
	buildOption('7:30pm'),
	buildOption('8:00pm'),
	buildOption('8:30pm'),
	buildOption('9:00pm'),
	buildOption('9:30pm'),
	buildOption('10:00pm'),
	buildOption('10:30pm'),
	buildOption('11:00pm'),
	buildOption('11:30pm'),
	buildOption('12:00am'),
	buildOption('12:30am'),
	buildOption('1:00am'),
	buildOption('1:30am'),
	buildOption('2:00am'),
	buildOption('2:30am'),
	buildOption('3:00am'),
	buildOption('3:30am'),
	buildOption('4:00am'),
	buildOption('4:30am'),
	buildOption('5:00am'),
	buildOption('5:30am'),
	buildOption('6:00am'),
	buildOption('6:30am'),
	buildOption('7:00am'),
	buildOption('7:30am'),
	buildOption('8:00am'),
	buildOption('8:30am'),
	buildOption('9:00am'),
	buildOption('9:30am'),
];

const propTypes = {

	/**
	 * The input name.
	 */
	name: PropTypes.string.isRequired,

	/**
	 * The input time value.
	 */
	value: PropTypes.string.isRequired,

	/**
	 * The input placeholder text (if any).
	 */
	placeholder: PropTypes.string,

	/**
	 * True, if the input time value is invalid.
	 */
	isInvalid: PropTypes.bool.isRequired,

	/**
	 * True, if the input time value is required.
	 */
	isRequired: PropTypes.bool.isRequired,

	/**
	 * The input on change callback function.
	 */
	onChange: PropTypes.func.isRequired,

};

const defaultProps = {
	isInvalid: false,
	isRequired: false,
};

/**
 * Formats the text to show when no option matches the search.
 *
 * @param {string} searchText  The search text.
 * @param {string} intl        The provider for internationalization.
 *
 * @returns {string} result    The formatted text label.
 */
const formatCreateLabel = (searchText, intl) => {

	return intl.formatMessage(
		messages.TC_COMPONENTS_TIME_PICKER_CREATE_OPTION_TEXT,
		{ value: searchText }
	);

};

/**
 * Determines when the create new option label text should be shown.
 *
 * @param   {string} inputValue     The input value.
 * @param   {object} selectedValue  The selected value.
 * @param   {array} options         The list of options to select.
 *
 * @returns {boolean}               Show/hide the create new option text.
 */
const isValidNewOption = (inputValue, selectedValue, options) => {

	return (
		Boolean(inputValue) &&
		options.filter((option) => option.value === inputValue).length === 0
	);

};

/**
 * Form component that can be used to select a specific time.
 *
 * @param {Object} props  The component props.
 *
 * @returns {Object}  The TimePicker component.
 */
const TimePicker = (props) => {

	const {
		name,
		value,
		placeholder,
		isInvalid,
		isRequired,
		onChange,
	} = props;

	const intl = useIntl();

	const componentOverrides = {
		DropdownIndicator: null, // Hide this
		IndicatorSeperator: null, // Hide this
	};

	if (isRequired) {
		componentOverrides.ClearIndicator = null; // Hide this
	}

	return (
		<CreatableSelect
			name={name}
			className={`tc-timepicker ${isInvalid ? 'is-invalid' : ''}`}
			classNamePrefix="tc-timepicker"
			options={TIME_OPTIONS}
			value={value ? { label: value, value } : null} // The selected value
			placeholder={placeholder}
			maxMenuHeight={200}
			// Text to show when no option matches the search
			formatCreateLabel={(searchText) => formatCreateLabel(searchText, intl)}
			// Determine if create label text should be shown
			isValidNewOption={isValidNewOption}
			isClearable={!isRequired}
			components={componentOverrides}
			onChange={(change) => onChange(change ? change.value : null)} />
	);

};

TimePicker.propTypes = propTypes;
TimePicker.defaultProps = defaultProps;

export default TimePicker;
